import {
  Button,
  ButtonGroup,
  Checkbox,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { TableFilters } from '../../../../../../../../components/TableFilters';
import { IProductBase } from '../../../../../../../../models/products';
import { listProductsService } from '../../../../../../../../services/Products/ListProductsService';

interface IHandleSelectProductProps {
  product: IProductBase;
  checked: boolean;
  index: number;
}

interface IProductsAssignModalProps {
  currentProductsId?: string[];
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (products: IProductBase[]) => void;
}

export const ProductsAssignModal = ({
  currentProductsId = [],
  isOpen,
  onClose,
  onSubmit,
}: IProductsAssignModalProps): JSX.Element => {
  const [productsList, setProductsList] = useState<IProductBase[]>([]);
  const [searchParam, setSearchParam] = useState<string>();
  const [selectedProducts, setSelectedProducts] = useState<IProductBase[]>([]);

  const allChecked = useMemo(
    () => selectedProducts.length === productsList.length,
    [productsList.length, selectedProducts.length],
  );
  const isIndeterminate = useMemo(
    () =>
      !!selectedProducts.length &&
      selectedProducts.length !== productsList.length,
    [productsList.length, selectedProducts.length],
  );

  useEffect(() => {
    setSelectedProducts(
      productsList.filter((product) => currentProductsId.includes(product.id)),
    );
  }, [currentProductsId, productsList]);

  useEffect(() => {
    async function loadProducts(search?: string): Promise<void> {
      const { items: products } = await listProductsService({ name: search });

      setProductsList(products);
    }

    loadProducts();
  }, []);

  const productsFound = useMemo(() => {
    if (searchParam) {
      return productsList.filter((product) =>
        product.name.match(new RegExp(searchParam, 'gi')),
      );
    }

    return productsList;
  }, [productsList, searchParam]);

  const handleProductSearch = useCallback((search: string) => {
    setSearchParam(search);
  }, []);

  const handleToggleSelectAllProducts = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.checked) {
        setSelectedProducts([]);
      } else {
        setSelectedProducts(productsFound);
      }
    },
    [productsFound],
  );

  const handleSelectProduct = useCallback(
    ({ product, checked }: IHandleSelectProductProps) => {
      if (!checked) {
        setSelectedProducts((prevState) =>
          prevState.filter(
            (selectedProduct) => selectedProduct.id !== product.id,
          ),
        );
      } else {
        setSelectedProducts((prevState) => [...prevState, product]);
      }
    },
    [],
  );

  const handleClose = useCallback(() => {
    setSelectedProducts([]);
    setSearchParam(undefined);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setSearchParam(undefined);
    onSubmit(selectedProducts);
  }, [onSubmit, selectedProducts]);

  return (
    <Modal
      size="5xl"
      scrollBehavior="inside"
      isOpen={isOpen}
      onClose={handleClose}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading size="lg" fontWeight="normal">
            Vincular produtos
          </Heading>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <TableFilters searchLabel="Buscar" onSearch={handleProductSearch} />

          <Table mt="4" colorScheme="blue">
            <Thead>
              <Tr>
                <Th px="6" w="8">
                  <Checkbox
                    colorScheme="blue"
                    borderColor="gray.300"
                    isChecked={allChecked}
                    isIndeterminate={isIndeterminate}
                    onChange={handleToggleSelectAllProducts}
                  />
                </Th>
                <Th>Nome</Th>
              </Tr>
            </Thead>

            <Tbody>
              {productsFound.map((product, index) => (
                <Tr key={product.id}>
                  <Td px="6">
                    <Checkbox
                      colorScheme="blue"
                      isChecked={
                        !!selectedProducts.find(
                          (feat) => feat.id === product.id,
                        )
                      }
                      borderColor="gray.300"
                      onChange={(event) =>
                        handleSelectProduct({
                          product,
                          checked: event.target.checked,
                          index,
                        })
                      }
                    />
                  </Td>

                  <Td>
                    <Text fontWeight="bold">{product.name}</Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button colorScheme="blackAlpha" onClick={handleClose}>
              Cancelar
            </Button>

            <Button colorScheme="green" onClick={handleSubmit}>
              Vincular
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
